import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Send, Copy, AlertCircle, AlertTriangle, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URGENCY_STYLES = {
  normal: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Heart, label: 'Normal' },
  important: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertCircle, label: 'Important' },
  urgent: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, label: 'Urgent' }
};

export const PrayerUpdatesTab = ({ onMessage }) => {
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState({
    title: '', content: '', title_no: '', content_no: '', urgency: 'normal', visibility: 'all_supporters'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const { data } = await axios.get(`${API}/prayer-updates`, { withCredentials: true });
      setUpdates(data);
    } catch (e) { console.error(e); }
  };

  const addUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/prayer-updates`, form, { withCredentials: true });
      setForm({ title: '', content: '', title_no: '', content_no: '', urgency: 'normal', visibility: 'all_supporters' });
      onMessage('Prayer update created');
      fetchAll();
    } catch (err) {
      onMessage(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const deleteUpdate = async (id) => {
    if (!window.confirm('Delete this prayer update?')) return;
    await axios.delete(`${API}/admin/prayer-updates/${id}`, { withCredentials: true });
    onMessage('Deleted');
    fetchAll();
  };

  const sendEmail = async (id, group) => {
    if (!window.confirm(`Send this prayer update via email to ${group === 'all' ? 'ALL supporters' : group === 'prayer' ? 'PRAYER supporters' : group}?`)) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/prayer-updates/${id}/send-email`,
        { update_id: id, recipient_group: group, selected_user_ids: [] },
        { withCredentials: true }
      );
      onMessage(`Email: ${data.sent}/${data.total} sent`);
    } catch (err) {
      onMessage(err.response?.data?.detail || 'Failed');
    }
  };

  const copyForWhatsApp = (upd) => {
    const text = `*${URGENCY_STYLES[upd.urgency].label} Prayer Request*\n\n*${upd.title}*\n\n${upd.content}\n\nPlease join us in prayer.`;
    navigator.clipboard.writeText(text).then(
      () => onMessage('Copied to clipboard — paste in WhatsApp!'),
      () => onMessage('Copy failed')
    );
  };

  return (
    <div data-testid="admin-prayer-updates-tab" className="space-y-6">
      {/* Create */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
          <Heart className="w-6 h-6 text-purple-600" /> Create Prayer Update
        </h2>
        <form data-testid="add-prayer-form" onSubmit={addUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="prayer-title-input" type="text" placeholder="🇬🇧 Prayer request title" required
                   value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
            <input data-testid="prayer-title-no-input" type="text" placeholder="🇳🇴 Norwegian title (optional)"
                   value={form.title_no} onChange={(e) => setForm({ ...form, title_no: e.target.value })}
                   className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <textarea data-testid="prayer-content-input" placeholder="🇬🇧 Prayer request details..." required rows="5"
                      value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
            <textarea data-testid="prayer-content-no-input" placeholder="🇳🇴 Norwegian content (optional)" rows="5"
                      value={form.content_no} onChange={(e) => setForm({ ...form, content_no: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Urgency</label>
              <select data-testid="prayer-urgency-select" value={form.urgency}
                      onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500">
                <option value="normal">Normal</option>
                <option value="important">Important</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Visible To</label>
              <select data-testid="prayer-visibility-select" value={form.visibility}
                      onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500">
                <option value="all_supporters">All Supporters</option>
                <option value="prayer_supporters">Prayer Supporters Only</option>
              </select>
            </div>
          </div>
          <button data-testid="add-prayer-submit-btn" type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50">
            <Plus className="w-5 h-5" /> {loading ? 'Creating...' : 'Create Prayer Update'}
          </button>
        </form>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Prayer Updates</h2>
        {updates.length === 0 ? (
          <p className="text-slate-600">No prayer updates yet.</p>
        ) : (
          <div className="space-y-4">
            {updates.map(upd => {
              const s = URGENCY_STYLES[upd.urgency] || URGENCY_STYLES.normal;
              const Icon = s.icon;
              return (
                <div key={upd.id} className="border border-slate-200 rounded-xl p-5">
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
                        <Icon className="w-3 h-3" /> {s.label}
                      </span>
                      <span className="text-xs text-slate-500">
                        {upd.visibility === 'prayer_supporters' ? '🤲 Prayer supporters' : '👥 All supporters'}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">{new Date(upd.created_at).toLocaleString()}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{upd.title}</h3>
                  <p className="text-slate-600 whitespace-pre-wrap mb-4">{upd.content}</p>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid={`copy-whatsapp-btn-${upd.id}`} onClick={() => copyForWhatsApp(upd)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-full text-sm font-medium hover:bg-green-700">
                      <Copy className="w-4 h-4" /> Copy for WhatsApp
                    </button>
                    <button data-testid={`email-prayer-all-btn-${upd.id}`} onClick={() => sendEmail(upd.id, 'all')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700">
                      <Send className="w-4 h-4" /> Email all
                    </button>
                    <button data-testid={`email-prayer-only-btn-${upd.id}`} onClick={() => sendEmail(upd.id, 'prayer')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-full text-sm font-medium hover:bg-purple-700">
                      <Send className="w-4 h-4" /> Email prayer team
                    </button>
                    <button data-testid={`delete-prayer-btn-${upd.id}`} onClick={() => deleteUpdate(upd.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
